/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { MAXIMUM_QUANTITY_PER_PRODUCT } from "@thkStore/Constants/ProductPropertiesKeys";
import ProductAddedToCartMessage from "@thkStore/Overrides/Components/ProductAddedToCartMessage";
import ProductQuantityWrapper from "@thkStore/Overrides/Components/ProductQuantityWrapper";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),        
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product,
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },        
    },    
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const toasterContext = useContext(ToasterContext);
    const maximumQuantityPerProduct = product.properties[MAXIMUM_QUANTITY_PER_PRODUCT] ? parseInt(product.properties[MAXIMUM_QUANTITY_PER_PRODUCT], 10) : 0;    

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }

        setProductQty(`${qtyOrdered}`);

    }, [qtyOrdered]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${product.id}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }

        if(maximumQuantityPerProduct > 0 && Number(possibleNumber) > maximumQuantityPerProduct)
        {
            showMaximumOrderQtyMessage();
        }
    };

    const handleButtonChange = (newValue: number) => {
        setProductQty(newValue.toString());

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${product.id}`);
            return;
        }

        onQtyOrderedChanged(newValue);

        if (maximumQuantityPerProduct > 0 && newValue > maximumQuantityPerProduct) {
            showMaximumOrderQtyMessage();
        }
    };

    const showMaximumOrderQtyMessage = () => {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage
                    isQtyAdjusted={false}
                    multipleProducts={false}
                    isMaximumOrderQty={true}
                    maximumOrderQty={maximumQuantityPerProduct}
                    productTitle={product.productTitle}
                    />,
                messageType: "warning",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
            });
    };

    return (
        <ProductQuantityWrapper
            value={productQty}
            onChange={handleButtonChange}
            onTextChange={qtyChangeHandler}
            onKeyDown={keyPressHandler}
            min={0}
            max={999999}
            label={labelOverride?.toString() ?? translate("QTY")}
            data-test-selector="product_qtyOrdered"
            extendedStyles={styles}
            textFieldProps={otherProps}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
